import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AnchorId, CommonStepId, StepActionKind } from './Enums';
import {
  NavSource,
  PanelState,
  type Subscene,
  type Step,
  type StepAction,
  type TourSubsceneId,
  type TourStepId,
  type TourDefinition,
  type TourModel,
} from './Types';

type TourContextValue = {
  tour: TourDefinition;
  stepId: TourStepId;
  stepIndex: number;
  currentStep?: Step;
  currentSubscene?: Subscene;
  subsceneId: TourSubsceneId | null;
  setSubscene: (
    id: TourSubsceneId | null,
    options?: { history?: 'push' | 'replace' }
  ) => void;
  model: TourModel;
  navSourceRef: React.MutableRefObject<NavSource | null>;
  navReplaceRef: React.MutableRefObject<boolean>;
  panel: PanelState;
  setPanel: (panel: PanelState) => void;
  togglePanel: () => void;
  anchorTick: number;
  registerAnchor: (key: AnchorId, el: HTMLElement | null) => void;
  unregisterAnchor: (key: AnchorId, el: HTMLElement | null) => void;
  getAnchorEl: (key: AnchorId) => HTMLElement | null;
  goTo: (id: TourStepId) => void;
  next: () => void;
  back: () => void;
  reset: () => void;
  executeAction: (action: StepAction) => void;
  getStep: (id: TourStepId) => Step | undefined;
  getStepIndex: (id: TourStepId) => number;
};

const TourContext = createContext<TourContextValue | null>(null);

const resolveDefaultSubsceneId = (step?: Step) => {
  if (!step?.subscenes || step.subscenes.length === 0) {
    return null;
  }
  if (
    step.defaultSubsceneId &&
    step.subscenes.some((subscene) => subscene.id === step.defaultSubsceneId)
  ) {
    return step.defaultSubsceneId;
  }
  return step.subscenes[0].id;
};

interface TourProviderProps {
  tour: TourDefinition;
  children: React.ReactNode;
  initialStepId?: string;
  initialSubsceneId?: string;
}

export const TourProvider = ({
  tour,
  children,
  initialStepId,
  initialSubsceneId,
}: TourProviderProps) => {
  const firstStepId = tour.steps[0]?.id ?? CommonStepId.TOUR_COMPLETE;
  const resolvedInitialStepId = (tour.steps.find(
    (step) => step.id === initialStepId
  )?.id ?? firstStepId) as TourStepId;
  const resolvedInitialStep =
    tour.steps.find((step) => step.id === resolvedInitialStepId) ??
    tour.steps[0];
  const resolvedInitialSubsceneId = (() => {
    if (
      !resolvedInitialStep?.subscenes ||
      resolvedInitialStep.subscenes.length === 0
    ) {
      return null;
    }
    if (
      initialSubsceneId &&
      resolvedInitialStep.subscenes.some(
        (subscene) => subscene.id === initialSubsceneId
      )
    ) {
      return initialSubsceneId as TourSubsceneId;
    }
    return resolveDefaultSubsceneId(resolvedInitialStep);
  })();
  const [stepId, setStepId] = useState<TourStepId>(resolvedInitialStepId);
  const [subsceneId, setSubsceneId] = useState<TourSubsceneId | null>(
    resolvedInitialSubsceneId
  );
  const [model, setModel] = useState<TourModel>(tour.initialModel);
  const [history, setHistory] = useState<TourStepId[]>([]);
  const navSourceRef = useRef<NavSource | null>(null);
  const navReplaceRef = useRef(false);
  const [panel, setPanel] = useState<PanelState>(PanelState.Collapsed);
  const [anchorTick, setAnchorTick] = useState(0);
  const anchorRegistryRef = useRef<Map<AnchorId, HTMLElement>>(new Map());

  const stepMap = useMemo(() => {
    const map: Partial<Record<TourStepId, { step: Step; index: number }>> = {};
    tour.steps.forEach((step, index) => {
      map[step.id] = { step, index };
    });
    return map;
  }, [tour.steps]);

  useEffect(() => {
    if (!stepMap[stepId] && firstStepId) {
      navSourceRef.current = NavSource.Route;
      setStepId(firstStepId);
    }
  }, [firstStepId, stepId, stepMap]);

  useEffect(() => {
    if (!initialStepId) {
      return;
    }
    const nextId =
      tour.steps.find((step) => step.id === initialStepId)?.id ?? firstStepId;
    if (nextId && nextId !== stepId) {
      navSourceRef.current = NavSource.Route;
      setHistory([]);
      setStepId(nextId);
    }
  }, [firstStepId, initialStepId, stepId, tour.steps]);

  const stepIndex = stepMap[stepId]?.index ?? 0;
  const currentStep = stepMap[stepId]?.step ?? tour.steps[0];
  const defaultSubsceneId = useMemo(
    () => resolveDefaultSubsceneId(currentStep),
    [currentStep]
  );
  const panelPreference = currentStep?.onEnter?.panel;
  const currentSubscene = useMemo(() => {
    if (!currentStep?.subscenes || currentStep.subscenes.length === 0) {
      return undefined;
    }
    if (subsceneId) {
      const matchedSubscene = currentStep.subscenes.find(
        (subscene) => subscene.id === subsceneId
      );
      if (matchedSubscene) {
        return matchedSubscene;
      }
    }
    return (
      currentStep.subscenes.find(
        (subscene) => subscene.id === defaultSubsceneId
      ) ?? currentStep.subscenes[0]
    );
  }, [currentStep, defaultSubsceneId, subsceneId]);

  useEffect(() => {
    if (panelPreference) {
      setPanel(panelPreference);
    }
  }, [panelPreference]);

  useEffect(() => {
    setSubsceneId(defaultSubsceneId);
  }, [defaultSubsceneId, stepId]);

  useEffect(() => {
    if (!currentStep?.subscenes || currentStep.subscenes.length === 0) {
      return;
    }
    if (!initialSubsceneId || navSourceRef.current === NavSource.Internal) {
      return;
    }
    const exists = currentStep.subscenes.some(
      (subscene) => subscene.id === initialSubsceneId
    );
    if (!exists || subsceneId === initialSubsceneId) {
      return;
    }
    navSourceRef.current = NavSource.Route;
    setSubsceneId(initialSubsceneId as TourSubsceneId);
  }, [currentStep, initialSubsceneId, subsceneId]);

  const resolveStepId = useCallback(
    (id: TourStepId | undefined) => {
      if (id && stepMap[id]) {
        return id;
      }
      return firstStepId;
    },
    [firstStepId, stepMap]
  );

  const getStep = useCallback(
    (id: TourStepId) => {
      return stepMap[id]?.step;
    },
    [stepMap]
  );

  const getStepIndex = useCallback(
    (id: TourStepId) => {
      return stepMap[id]?.index ?? 0;
    },
    [stepMap]
  );

  const navigateTo = useCallback(
    (targetId: TourStepId, pushHistory = true) => {
      const resolvedId = resolveStepId(targetId);
      if (!resolvedId || resolvedId === stepId) {
        return;
      }
      navSourceRef.current = NavSource.Internal;
      navReplaceRef.current = false;
      const shouldPush = pushHistory;
      setHistory((prev) => (shouldPush ? [...prev, stepId] : prev));
      setStepId(resolvedId);
    },
    [resolveStepId, stepId]
  );

  const getNextId = useCallback(() => {
    if (currentStep?.next) {
      return resolveStepId(currentStep.next);
    }
    const sequentialId = tour.steps[stepIndex + 1]?.id;
    return sequentialId ? resolveStepId(sequentialId) : stepId;
  }, [currentStep, resolveStepId, stepIndex, stepId, tour.steps]);

  const getBackId = useCallback(() => {
    if (currentStep?.back) {
      return resolveStepId(currentStep.back);
    }
    const sequentialId = tour.steps[stepIndex - 1]?.id;
    return sequentialId ? resolveStepId(sequentialId) : stepId;
  }, [currentStep, resolveStepId, stepIndex, stepId, tour.steps]);

  const goTo = useCallback(
    (id: TourStepId) => {
      navigateTo(id, true);
    },
    [navigateTo]
  );

  const next = useCallback(() => {
    navigateTo(getNextId(), true);
  }, [getNextId, navigateTo]);

  const back = useCallback(() => {
    setHistory((prev) => {
      if (prev.length === 0) {
        const fallbackId = getBackId();
        if (fallbackId && fallbackId !== stepId) {
          navSourceRef.current = NavSource.Internal;
          navReplaceRef.current = false;
          setStepId(fallbackId);
        }
        return prev;
      }
      const nextHistory = [...prev];
      const targetId = nextHistory.pop();
      if (targetId && targetId !== stepId) {
        navSourceRef.current = NavSource.Internal;
        navReplaceRef.current = false;
        setStepId(resolveStepId(targetId));
      }
      return nextHistory;
    });
  }, [getBackId, resolveStepId, stepId]);

  const reset = useCallback(() => {
    navSourceRef.current = NavSource.Internal;
    navReplaceRef.current = false;
    setHistory([]);
    setStepId(firstStepId);
    setModel(tour.initialModel);
    setPanel(PanelState.Collapsed);
  }, [firstStepId, tour.initialModel]);

  const executeAction = useCallback(
    (action: StepAction) => {
      switch (action.action) {
        case StepActionKind.NEXT:
          next();
          return;
        case StepActionKind.BACK:
          back();
          return;
        case StepActionKind.GO_TO:
          goTo(action.to);
          return;
        case StepActionKind.RESET:
          reset();
          return;
        case StepActionKind.EXIT:
          return;
        default:
          return;
      }
    },
    [back, goTo, next, reset]
  );

  const togglePanel = useCallback(() => {
    setPanel((prev) =>
      prev === PanelState.Collapsed ? PanelState.Expanded : PanelState.Collapsed
    );
  }, []);

  const setSubscene = useCallback(
    (id: TourSubsceneId | null, options?: { history?: 'push' | 'replace' }) => {
      if (!currentStep?.subscenes || currentStep.subscenes.length === 0) {
        return;
      }
      const nextSubsceneId = (() => {
        if (!id) {
          return defaultSubsceneId;
        }
        const exists = currentStep.subscenes.some(
          (subscene) => subscene.id === id
        );
        if (exists) {
          return id;
        }
        return null;
      })();
      if (!nextSubsceneId || nextSubsceneId === subsceneId) {
        return;
      }
      navSourceRef.current = NavSource.Internal;
      navReplaceRef.current = options?.history === 'replace';
      setSubsceneId(nextSubsceneId);
    },
    [currentStep, defaultSubsceneId, subsceneId]
  );

  const registerAnchor = useCallback(
    (key: AnchorId, el: HTMLElement | null) => {
      if (!key || !el) {
        return;
      }
      const current = anchorRegistryRef.current.get(key);
      if (current === el) {
        return;
      }
      anchorRegistryRef.current.set(key, el);
      setAnchorTick((prev) => prev + 1);
    },
    []
  );

  const unregisterAnchor = useCallback(
    (key: AnchorId, el: HTMLElement | null) => {
      if (!key) {
        return;
      }
      const current = anchorRegistryRef.current.get(key);
      if (!el || current === el) {
        const didDelete = anchorRegistryRef.current.delete(key);
        if (didDelete) {
          setAnchorTick((prev) => prev + 1);
        }
      }
    },
    []
  );

  const getAnchorEl = useCallback((key: AnchorId) => {
    if (!key) {
      return null;
    }
    return anchorRegistryRef.current.get(key) ?? null;
  }, []);

  const value = useMemo(
    () => ({
      tour,
      stepId,
      stepIndex,
      currentStep,
      currentSubscene,
      subsceneId,
      setSubscene,
      model,
      panel,
      setPanel,
      togglePanel,
      anchorTick,
      navSourceRef,
      navReplaceRef,
      registerAnchor,
      unregisterAnchor,
      getAnchorEl,
      goTo,
      next,
      back,
      reset,
      executeAction,
      getStep,
      getStepIndex,
    }),
    [
      tour,
      stepId,
      stepIndex,
      currentStep,
      currentSubscene,
      subsceneId,
      setSubscene,
      model,
      panel,
      setPanel,
      togglePanel,
      anchorTick,
      navSourceRef,
      navReplaceRef,
      registerAnchor,
      unregisterAnchor,
      getAnchorEl,
      goTo,
      next,
      back,
      reset,
      executeAction,
      getStep,
      getStepIndex,
    ]
  );

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
};

export const useTour = () => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within a TourProvider.');
  }
  return context;
};
